import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

type QuoteItem = {
  id: number;
  product_name: string;
  color_style?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
};

type QuoteData = {
  id: number;
  status: string;
  currency: string;
  discount_percent?: number;
  tax_rate?: number;
  subtotal: number;
  tax_amount?: number;
  total: number;
  valid_until?: string;
  payment_terms?: string;
  notes?: string;
  confirmed_at?: string;
  sent_at?: string;
  contacts?: { first_name: string; last_name: string; email_jsonb?: { email: string }[] };
  companies?: { name: string };
  created_by_sales?: { first_name: string; last_name: string };
  items: QuoteItem[];
};

const formatCurrency = (amount: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount ?? 0);

export const QuotationPublicPage = () => {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${SUPABASE_URL}/functions/v1/get-quote?token=${token}`)
      .then((r) => {
        if (!r.ok) throw new Error("Quote not found");
        return r.json();
      })
      .then((data) => {
        setQuote(data);
        if (data.status === "confirmed") setConfirmed(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setConfirming(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/get-quote?token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm" }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || "Failed to confirm");
      }
      setConfirmed(true);
      setQuote((q) => q ? { ...q, status: "confirmed", confirmed_at: new Date().toISOString() } : q);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to confirm");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-lg font-medium text-gray-700">Quote not found</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const customerName = quote.contacts
    ? `${quote.contacts.first_name} ${quote.contacts.last_name}`
    : "Valued Customer";
  const salesName = quote.created_by_sales
    ? `${quote.created_by_sales.first_name} ${quote.created_by_sales.last_name}`
    : "";
  const quoteNumber = `QUO-${String(quote.id).padStart(4, "0")}`;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-slate-800 text-white px-8 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">Proforma Quotation</h1>
              <p className="text-slate-300 text-sm mt-1">{quoteNumber}</p>
            </div>
            <Badge
              className={`mt-1 ${
                quote.status === "confirmed"
                  ? "bg-green-500 text-white border-green-500"
                  : "bg-yellow-400 text-slate-800 border-yellow-400"
              }`}
            >
              {quote.status === "confirmed" ? "Confirmed" : "Awaiting Confirmation"}
            </Badge>
          </div>
        </div>

        <div className="px-8 py-6 space-y-6">
          {/* Customer & dates */}
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Prepared for</div>
              <div className="font-semibold">{customerName}</div>
              {quote.companies && <div className="text-gray-600">{quote.companies.name}</div>}
              {quote.contacts?.email_jsonb?.[0]?.email && (
                <div className="text-gray-500">{quote.contacts.email_jsonb[0].email}</div>
              )}
            </div>
            <div className="text-right">
              {salesName && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your contact</div>
                  <div className="font-semibold">{salesName}</div>
                </div>
              )}
              {quote.valid_until && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Valid until</div>
                  <div className="font-semibold">
                    {new Date(quote.valid_until).toLocaleDateString("en-US", {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Items */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Products
            </h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <th className="text-left px-4 py-2">Product</th>
                    <th className="text-right px-4 py-2 w-20">Qty</th>
                    <th className="text-right px-4 py-2 w-28">Unit Price</th>
                    <th className="text-right px-4 py-2 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((item, i) => (
                    <tr key={item.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.product_name}</div>
                        {item.color_style && (
                          <div className="text-xs text-gray-500">{item.color_style}</div>
                        )}
                      </td>
                      <td className="text-right px-4 py-3">{item.quantity}</td>
                      <td className="text-right px-4 py-3">
                        {formatCurrency(item.unit_price, quote.currency)}
                      </td>
                      <td className="text-right px-4 py-3 font-medium">
                        {formatCurrency(item.total_price, quote.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span>{formatCurrency(quote.subtotal, quote.currency)}</span>
              </div>
              {(quote.discount_percent ?? 0) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({quote.discount_percent}%)</span>
                  <span>− {formatCurrency(quote.subtotal * (quote.discount_percent! / 100), quote.currency)}</span>
                </div>
              )}
              {(quote.tax_rate ?? 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Tax ({quote.tax_rate}%)</span>
                  <span>{formatCurrency(quote.tax_amount ?? 0, quote.currency)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-base font-bold">
                <span>Total</span>
                <span>{formatCurrency(quote.total, quote.currency)}</span>
              </div>
            </div>
          </div>

          {/* Terms & notes */}
          {(quote.payment_terms || quote.notes) && (
            <>
              <Separator />
              <div className="space-y-3 text-sm">
                {quote.payment_terms && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment Terms</div>
                    <div className="text-gray-700">{quote.payment_terms}</div>
                  </div>
                )}
                {quote.notes && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Notes</div>
                    <div className="text-gray-700 whitespace-pre-line">{quote.notes}</div>
                  </div>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Confirmation section */}
          {confirmed ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
              <div>
                <p className="font-semibold text-green-800">Quotation Confirmed</p>
                {quote.confirmed_at && (
                  <p className="text-sm text-green-600">
                    Confirmed on {new Date(quote.confirmed_at).toLocaleString("en-US")}
                  </p>
                )}
              </div>
            </div>
          ) : quote.status === "sent" ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-gray-600">
                By clicking "Confirm Quotation", you agree to the terms and pricing above.
              </p>
              <Button
                size="lg"
                className="w-full sm:w-auto min-w-48"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirming...</>
                ) : (
                  "Confirm Quotation"
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-500 py-2">
              This quotation is <strong>{quote.status}</strong> and is no longer awaiting confirmation.
            </div>
          )}
        </div>

        <div className="px-8 py-4 bg-gray-50 border-t text-center text-xs text-gray-400">
          This is a formal quotation. Please contact your sales representative with any questions.
        </div>
      </div>
    </div>
  );
};

QuotationPublicPage.path = "/q/public/:token";
